import { Injectable } from '@angular/core';
import {
  collection,
  collectionData,
  documentId,
  Firestore,
  getDocs,
  query,
  QuerySnapshot,
  where,
} from '@angular/fire/firestore';
import { getUserInfo$, updateUserDoc, UserInfoWithDocId } from '@yoimo/client-sdk/users';
import { chunk } from 'lodash';
import { firstValueFrom, Observable } from 'rxjs';
import { UserData } from '../interfaces/user';
import { Role } from './userSession.service';

type ExtendedUserInfoWithDocId = Partial<UserInfoWithDocId> & {
  role: Role;
};

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(private readonly firestore: Firestore) {}

  getUsersData() {
    const usersCollection = collection(this.firestore, 'users');
    const usersQuery = query(
      usersCollection,
      where('role', 'in', [
        Role.ADMIN,
        Role.OTT,
        Role.TECH,
        Role.FINANCE,
        Role.CSM,
        Role.SALES,
      ])
    );

    return collectionData(usersQuery, { idField: 'docId' }) as Observable<UserData[]>;
  }
  
  getUserByDocId(id: string): Promise<ExtendedUserInfoWithDocId | undefined> {
    return firstValueFrom(getUserInfo$(id, this.firestore)) as Promise<ExtendedUserInfoWithDocId | undefined>;
  }

  checkUser(
    email: string,
    environmentName: string,
    selectedEmailType?: string
  ) {
    let filterRef: string[] = [];
    if (environmentName == 'bucs') {
      if (selectedEmailType == '@bucs.tv') {
        filterRef = [`${email}@bucs.tv`, `${email}@ncompass.inc`];
      } else {
        filterRef = [`${email}@joymo.tv`, `${email}@ncompass.inc`];
      }
    } else {
      filterRef = [`${email}@joymo.tv`, `${email}@ncompass.inc`];
    }

    const usersCollection = collection(this.firestore, 'users');
    const usersQuery = query(usersCollection, where('email', 'in', filterRef));

    return collectionData(usersQuery, { idField: 'docId' }) as Observable<UserData[]>;
  }

  getUserByEmail(email: string) {
    const usersCollection = collection(this.firestore, 'users');
    const usersQuery = query(usersCollection, where('email', '==', email));
    
    return collectionData(usersQuery, { idField: 'docId' }) as Observable<UserData[]>;
  }
  
  addUser(role: Role, id: string): Promise<void> {
    const updateData = {
      role: role,
      archived: false,
      createdTime: new Date(),
    };
    return firstValueFrom(updateUserDoc(this.firestore, id, updateData));
  }

  updateUser(role: Role, id: string): Promise<void> {
    const updateData = {
      archived: false,
      role: role
    };
    return firstValueFrom(updateUserDoc(this.firestore, id, updateData));
  }

  updateUserActivity(id: string, lastActivity: Date): Promise<void> {
    const updateData = {
      lastActivity: lastActivity
    };
    return firstValueFrom(updateUserDoc(this.firestore, id, updateData));
  }

  deleteUser(id: string): Promise<void> {
    const updateData: Partial<UserData> = {
      archived: true,
      role: null
    };
    return firstValueFrom(updateUserDoc(this.firestore, id, updateData));
  }

  async getUsersByBatch(idList: string[]): Promise<UserData[]> {
    return (
      await Promise.all(
        chunk(idList, 10).map((slice) => {
          const usersCollection = collection(this.firestore, 'users');
          const usersQuery = query(
            usersCollection,
            where(documentId(), 'in', slice)
          );
          return getDocs(usersQuery) as Promise<QuerySnapshot<UserData>>;
        })
      )
    ).reduce(
      (acc: any, batchResult) => [...acc, ...batchResult.docs],
      []
    ).map((doc: any) => {
      const data = doc.data() as UserData;
      return {
        ...data,
        docId: doc.id,
      };
    });
  }
}
